import { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Zap } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Button, Field, Input, Select, Textarea } from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import { PhotoCaptureField } from "@/features/assets/PhotoCaptureField";
import { uploadAssetPhoto } from "@/features/assets/assetPhotoUpload";

// =============================================================================
// PowerToolNewScreen — /assets/power-tools/new (HR hub, manager+) and
//                      /inspections/add/power-tool (HSE hub, every employee)
// Create form for a power tool / charger / battery / IT item. Open to any
// authenticated user so crew can self-register gear bought on the road.
// Optional phone-camera photo uploaded post-insert. T-051 Part B.
//
// 12 Oct 2026 — also mounted under /inspections so crews can reach it (the
// /assets route sits in the HR hub, which field logins don't hold). From the
// register the back/done links stay in the register. Kit is a picker over
// existing kits; who holds it is manager/supervisor only — a crew member's
// item lands in the Pool for a manager to allocate. Photo is manager+ only
// (asset_photos is manager-write under RLS). Same rules as RiggingNewScreen.
// =============================================================================

const CATEGORIES = ["Power Tool", "Battery", "Charger", "IT", "Other"] as const;
const CONDITIONS = ["new", "good", "fair", "poor", "decommissioned"] as const;

export default function PowerToolNewScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  const fromRegister = location.pathname.startsWith("/inspections");
  const backHref     = fromRegister ? "/inspections?kind=power_tool" : "/assets";
  const canAllocate  = hasRole(user, "supervisor");
  const canPhoto     = hasRole(user, "manager");

  // T-253 — tool_code is system-generated by the DB default (mig 145).
  const [asset_tag_number, setAssetTag] = useState("");
  const [display_name, setDisplayName] = useState("");
  const [tool_category, setCategory]   = useState<string>("Power Tool");
  const [tool_type, setToolType]       = useState("");
  const [size_spec, setSizeSpec]       = useState("");
  const [make, setMake]                = useState("Milwaukee");
  const [model, setModel]              = useState("");
  const [serial_number, setSerial]     = useState("");
  const [batch_lot_number, setBatchLot] = useState("");
  const [purchase_date, setPurchaseDate]   = useState("");
  const [purchase_price, setPurchasePrice] = useState("");
  const [purchase_supplier, setSupplier]   = useState("");
  const [condition, setCondition]          = useState<string>("new");
  // Default: most workplace tools need test &amp; tag (AS/NZS 3760). Cordless
  // and battery-powered tools that never plug into mains can be unticked
  // — they fall out of the Inspection Register entirely (mig 097 filter).
  const [inspection_required, setInspReq]  = useState(true);
  const [inspection_interval_months, setInspInterval] = useState("6");
  const [kit_name, setKitName]             = useState("");
  const [assigned_to_employee_id, setAssignedTo] = useState("");
  const [notes, setNotes]                  = useState("");
  const [photo, setPhoto]                  = useState<File | null>(null);
  const [errMsg, setErrMsg]                = useState<string | null>(null);

  const { data: employees = [] } = useQuery({
    queryKey: ["employees_active"],
    enabled: canAllocate,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees").select("employee_id, first_name, last_name")
        .eq("is_active", true).order("first_name");
      if (error) throw error;
      return data ?? [];
    },
  });

  // Existing kits across power tools and rigging — pick, never retype.
  const { data: kitRows = [] } = useQuery({
    queryKey: ["tool_kits"],
    queryFn: async () => {
      const [a, b] = await Promise.all([
        supabase.from("power_tools").select("kit_name").not("kit_name", "is", null),
        supabase.from("rigging_equipment").select("kit_name").not("kit_name", "is", null),
      ]);
      if (a.error) throw a.error;
      if (b.error) throw b.error;
      return [...(a.data ?? []), ...(b.data ?? [])] as { kit_name: string | null }[];
    },
  });
  const kits = useMemo(() => {
    const set = new Set<string>();
    for (const r of kitRows) if (r.kit_name) set.add(r.kit_name);
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [kitRows]);

  const createMut = useMutation({
    mutationFn: async () => {
      // tool_code intentionally omitted — DB default mints it (T-253, mig 145)
      if (!display_name.trim()) throw new Error("Display name is required.");
      const { data, error } = await supabase
        .from("power_tools")
        .insert({
          // tool_code: omitted — DB default mints it (mig 145)
          asset_tag_number: asset_tag_number.trim() || null, // mig 145 trigger defaults to tool_code if null
          display_name: display_name.trim(),
          tool_category: tool_category || null,
          tool_type: tool_type.trim() || null,
          size_spec: size_spec.trim() || null,
          make: make.trim() || null,
          model: model.trim() || null,
          serial_number: serial_number.trim() || null,
          batch_lot_number: batch_lot_number.trim() || null,
          purchase_date: purchase_date || null,
          purchase_price: purchase_price ? parseFloat(purchase_price) : null,
          purchase_supplier: purchase_supplier.trim() || null,
          condition: condition || null,
          inspection_required,
          inspection_interval_months: inspection_interval_months ? parseInt(inspection_interval_months, 10) : 6,
          kit_name: canAllocate ? (kit_name.trim() || null) : null,
          assigned_to_employee_id: canAllocate ? (assigned_to_employee_id || null) : null,
          notes: notes.trim() || null,
          is_active: true,
        })
        .select("tool_id")
        .single();
      if (error) throw error;
      const newId = data.tool_id as string;
      if (photo && canPhoto) {
        try { await uploadAssetPhoto("power_tool", newId, photo); }
        catch (e) { console.warn("Power tool photo upload failed:", e); }
      }
      return newId;
    },
    onSuccess: (toolId) => {
      qc.invalidateQueries({ queryKey: ["assets"] });
      qc.invalidateQueries({ queryKey: ["inspection_register"] });
      qc.invalidateQueries({ queryKey: ["tool_kits"] });
      navigate(fromRegister ? `/inspections/equipment/power_tool/${toolId}` : `/assets/power-tools/${toolId}`);
    },
    onError: (e: unknown) => setErrMsg(e instanceof Error ? e.message : "Create failed"),
  });

  const canSubmit = !!display_name.trim() && !createMut.isPending;

  return (
    <div className="p-6 max-w-3xl mx-auto pb-28">
      <button onClick={() => navigate(backHref)} className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3" type="button">
        <ArrowLeft size={14} /> {fromRegister ? "Back to the register" : "Back to assets"}
      </button>
      <h1 className="text-[20px] font-semibold text-text flex items-center gap-2 mb-5">
        <Zap size={20} className="text-accent" /> New power tool
      </h1>

      <Card>
        {canPhoto && (
          <div className="p-5 border-b border-line">
            <PhotoCaptureField
              value={photo}
              onChange={setPhoto}
              label="Reference photo"
              hint="Snap the tool now — helps everyone identify it in the register later."
            />
          </div>
        )}
        <div className="p-5 border-b border-line">
          <div className="text-[13px] font-medium text-text mb-3">Identity</div>
          {/* T-253 — code auto-generated by the DB on save (PWR-NNNN). */}
          <div className="mb-3 px-3 py-2 rounded bg-accent/5 border border-accent/20 text-[12px] text-text-muted">
            <span className="font-mono text-accent">PWR-NNNN</span> code generated automatically on save. No need to type one.
          </div>
          <Field label="Asset tag" hint="Physical sticker number. Defaults to the auto-generated code if blank.">
            <Input mono value={asset_tag_number} onChange={e => setAssetTag(e.target.value)} autoFocus />
          </Field>
          <Field label="Display name *">
            <Input value={display_name} onChange={e => setDisplayName(e.target.value)} placeholder='e.g. 1/2" Impact Driver' />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={tool_category} onChange={e => setCategory(e.target.value)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Type" hint="Free text, e.g. Impact Driver, Battery, Charger.">
              <Input value={tool_type} onChange={e => setToolType(e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Size / spec">
              <Input value={size_spec} onChange={e => setSizeSpec(e.target.value)} placeholder='e.g. 6.0ah, 1/2"' />
            </Field>
            <Field label="Make">
              <Input value={make} onChange={e => setMake(e.target.value)} />
            </Field>
            <Field label="Model">
              <Input value={model} onChange={e => setModel(e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Serial number">
              <Input mono value={serial_number} onChange={e => setSerial(e.target.value)} />
            </Field>
            <Field label="Batch / lot">
              <Input mono value={batch_lot_number} onChange={e => setBatchLot(e.target.value)} />
            </Field>
          </div>
        </div>

        <div className="p-5 border-b border-line">
          <div className="text-[13px] font-medium text-text mb-3">Lifecycle</div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Purchase date">
              <Input type="date" mono value={purchase_date} onChange={e => setPurchaseDate(e.target.value)} />
            </Field>
            <Field label="Price">
              <Input type="number" mono value={purchase_price} onChange={e => setPurchasePrice(e.target.value)} placeholder="ex-GST" />
            </Field>
            <Field label="Supplier">
              <Input value={purchase_supplier} onChange={e => setSupplier(e.target.value)} />
            </Field>
          </div>
          <Field label="Condition">
            <Select value={condition} onChange={e => setCondition(e.target.value)}>
              {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>
        </div>

        <div className="p-5 border-b border-line">
          <div className="text-[13px] font-medium text-text mb-3">Test &amp; tag</div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={inspection_required}
              onChange={e => setInspReq(e.target.checked)}
              className="accent-accent mt-1"
            />
            <span className="text-[13px] text-text">
              Requires test &amp; tag
              <span className="block text-[11px] text-text-muted mt-0.5">
                Tick for mains-powered tools (AS/NZS 3760). Untick for
                cordless / battery tools — they won't appear in the
                Inspection Register.
              </span>
            </span>
          </label>
          {inspection_required && (
            <div className="mt-3">
              <Field label="Interval (months)" hint="Standard 3 months for Coretel kit; AS/NZS 3760 maximum is 12.">
                <Input type="number" mono value={inspection_interval_months} onChange={e => setInspInterval(e.target.value)} />
              </Field>
            </div>
          )}
        </div>

        <div className="p-5">
          <div className="text-[13px] font-medium text-text mb-3">Assignment &amp; notes</div>
          {canAllocate ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Kit" hint="Pick an existing kit. Only use New kit for a genuinely new one — match the spelling of the others.">
                <Select
                  value={kit_name}
                  onChange={e => {
                    if (e.target.value === "__new__") {
                      const name = window.prompt("New kit name (match the spelling of the others exactly):")?.trim();
                      setKitName(name ?? "");
                    } else {
                      setKitName(e.target.value);
                    }
                  }}
                >
                  <option value="">— Pool (spare) —</option>
                  {(kits.includes(kit_name) || !kit_name ? kits : [...kits, kit_name].sort()).map(k => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                  <option value="__new__">New kit…</option>
                </Select>
              </Field>
              <Field label="Assigned to">
                <Select value={assigned_to_employee_id} onChange={e => setAssignedTo(e.target.value)}>
                  <option value="">— Pool —</option>
                  {employees.map(e => {
                    const name = `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim();
                    return <option key={e.employee_id} value={e.employee_id}>{name}</option>;
                  })}
                </Select>
              </Field>
            </div>
          ) : (
            <div className="mb-3 px-3 py-2 rounded bg-ink-raised border border-line text-[12px] text-text-muted">
              This item goes into the <span className="text-text">Pool</span>. A manager or supervisor allocates it to a kit and a person from HR → Assets.
            </div>
          )}
          <Field label="Notes">
            <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </Field>
        </div>
      </Card>

      <StickyBottomBar>
        {errMsg && (
          <div className="px-3 py-2 bg-red-900/30 border-b border-red-900/50 text-red-200 text-[12px] text-center">{errMsg}</div>
        )}
        <div className="p-3 flex justify-center">
          <div className="flex gap-2 max-w-2xl w-full">
            <Button variant="secondary" size="lg" onClick={() => navigate(backHref)} type="button">Cancel</Button>
            <Button size="lg" icon={CheckCircle2} onClick={() => createMut.mutate()} disabled={!canSubmit} className="flex-1 justify-center" type="button">
              {createMut.isPending ? "Creating…" : "Create power tool"}
            </Button>
          </div>
        </div>
      </StickyBottomBar>
    </div>
  );
}
